import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';

import { getInvocationStats, invocations } from '../utils/invocations-tracker';

interface FunctionInvocationData {
    name: string;
    stats: {
        avg10min: number;
        avg5min: number;
        avg1min: number;
        max: number;
    };
    count: number;
}

export const InvocationsTracker = () => {
    const [trackerData, setTrackerData] = useState<FunctionInvocationData[]>([]);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

    const refreshData = () => {
        const data: FunctionInvocationData[] = [];

        // Get all unique function names from invocations
        const functionNames = Object.keys(invocations);

        // Create data array with stats for each function
        functionNames.forEach(name => {
            const stats = getInvocationStats(name);
            const count = invocations[name]?.length || 0;

            data.push({
                name,
                stats,
                count,
            });
        });

        // Sort by most frequently called functions
        data.sort((a, b) => b.count - a.count);

        setTrackerData(data);
        setLastUpdated(new Date());
    };

    // Initial data load and refresh interval
    useEffect(() => {
        refreshData();

        const intervalId = setInterval(refreshData, 3000);
        return () => clearInterval(intervalId);
    }, []);

    const formatNumber = (num: number): string => {
        return num.toFixed(2);
    };

    return (
        <Column padding="s16">
            <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                Last updated: {lastUpdated.toLocaleTimeString()}
            </Text>
            <SizedBox value={16} />

            {trackerData.length === 0 ? (
                <Box padding="s16" alignItems="center">
                    <Text variant="bodyMedium" color={ds.colorToken.text.secondary}>
                        No function invocations recorded yet
                    </Text>
                </Box>
            ) : (
                <>
                    {/* Table Header */}
                    <Row
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        gap={'s2'}
                        padding="s12"
                        borderTopLeftRadius={'r8'}
                        borderTopRightRadius={'r8'}
                    >
                        <Box flex={2}>
                            <Text variant="titleSmall">Function</Text>
                        </Box>
                        <Box flex={1} alignItems="center">
                            <Text variant="titleSmall">1m</Text>
                        </Box>
                        <Box flex={1} alignItems="center">
                            <Text variant="titleSmall">5m</Text>
                        </Box>
                        <Box flex={1} alignItems="center">
                            <Text variant="titleSmall">10m</Text>
                        </Box>
                        <Box flex={1} alignItems="center">
                            <Text variant="titleSmall">Max (10s)</Text>
                        </Box>
                        <Box flex={1} alignItems="center">
                            <Text variant="titleSmall">Count</Text>
                        </Box>
                    </Row>

                    <ScrollView style={styles.tableContainer}>
                        {trackerData.map((item, index) => (
                            <React.Fragment key={item.name}>
                                <Row
                                    gap={'s2'}
                                    padding="s12"
                                    backgroundColor={
                                        index % 2 === 0
                                            ? ds.colorToken.background.neutral.subtler
                                            : ds.colorToken.background.neutral.base
                                    }
                                >
                                    <Box flex={2}>
                                        <Text variant="bodyMedium" numberOfLines={1} ellipsizeMode="middle">
                                            {item.name}
                                        </Text>
                                    </Box>
                                    <Box flex={1} alignItems="center">
                                        <Text variant="bodyMedium">{formatNumber(item.stats.avg1min)}/s</Text>
                                    </Box>
                                    <Box flex={1} alignItems="center">
                                        <Text variant="bodyMedium">{formatNumber(item.stats.avg5min)}/s</Text>
                                    </Box>
                                    <Box flex={1} alignItems="center">
                                        <Text variant="bodyMedium">{formatNumber(item.stats.avg10min)}/s</Text>
                                    </Box>
                                    <Box flex={1} alignItems="center">
                                        <Text variant="bodyMedium">{item.stats.max}</Text>
                                    </Box>
                                    <Box flex={1} alignItems="center">
                                        <Text variant="bodyMedium">{item.count}</Text>
                                    </Box>
                                </Row>
                                {index < trackerData.length - 1 && <LineSeparator />}
                            </React.Fragment>
                        ))}
                    </ScrollView>

                    <SizedBox value={16} />
                    <Box borderRadius="r8" backgroundColor={ds.colorToken.background.neutral.subtler} padding="s12">
                        <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                            • avg1min, avg5min, avg10min: Average invocations per second in the last 1, 5, or 10 minutes
                        </Text>
                        <SizedBox value={8} />
                        <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                            • max: Highest number of invocations in any 10-second window
                        </Text>
                        <SizedBox value={8} />
                        <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                            • count: Total invocations in the tracked period (last 10 minutes)
                        </Text>
                    </Box>
                </>
            )}
        </Column>
    );
};

const styles = StyleSheet.create({
    tableContainer: {
        maxHeight: 400,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
});
